import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { CreateRoomPayload, JoinRoomPayload, RoomListQuery, UpdateRoomPayload, CreateRoomInvitePayload } from '@matchroom/shared';
import { roomService } from '@/services/room.service';
import { queryKeys } from '@/lib/query-client';
import { useRoomStore } from '@/stores/room.store';
import { useParticipantStore } from '@/stores/participant.store';
import { ApiClientError } from '@/lib/api-client';

export function useRoomsQuery(params?: RoomListQuery, enabled = true) {
  const setRooms = useRoomStore((s) => s.setRooms);
  const setLoading = useRoomStore((s) => s.setLoading);

  return useQuery({
    queryKey: [...queryKeys.rooms.all, params],
    enabled,
    queryFn: async () => {
      setLoading(true);
      try {
        const result = await roomService.list(params);
        setRooms(result.data);
        return result;
      } finally {
        setLoading(false);
      }
    },
  });
}

export function useRoomQuery(id: string, enabled = true) {
  const setCurrentRoom = useRoomStore((s) => s.setCurrentRoom);

  return useQuery({
    queryKey: queryKeys.rooms.detail(id),
    queryFn: async () => {
      const result = await roomService.getById(id);
      setCurrentRoom(result.room);
      return result;
    },
    enabled: enabled && !!id,
  });
}

export function useCreateRoomMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateRoomPayload) => roomService.create(payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.rooms.all }),
  });
}

export function useJoinRoomMutation() {
  const queryClient = useQueryClient();
  const setCurrentRoom = useRoomStore((s) => s.setCurrentRoom);

  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload?: JoinRoomPayload }) =>
      roomService.join(id, payload),
    onSuccess: (data) => {
      setCurrentRoom(data.room);
      queryClient.setQueryData(queryKeys.rooms.detail(data.room.id), { room: data.room });
    },
  });
}

export function useLeaveRoomMutation() {
  const queryClient = useQueryClient();
  const clear = useRoomStore((s) => s.clear);

  return useMutation({
    mutationFn: (id: string) => roomService.leave(id),
    onSuccess: () => {
      clear();
      queryClient.invalidateQueries({ queryKey: queryKeys.rooms.all });
    },
  });
}

export function useDeleteRoomMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => roomService.delete(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.rooms.all }),
  });
}

export function useUpdateRoomMutation() {
  const queryClient = useQueryClient();
  const updateCurrentRoom = useRoomStore((s) => s.updateCurrentRoom);

  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateRoomPayload }) =>
      roomService.update(id, payload),
    onSuccess: (data) => {
      updateCurrentRoom(data.room);
      queryClient.setQueryData(queryKeys.rooms.detail(data.room.id), { room: data.room });
    },
  });
}

export function useCreateInviteMutation() {
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload?: CreateRoomInvitePayload }) =>
      roomService.createInvite(id, payload),
  });
}

export function useTransferHostMutation() {
  const queryClient = useQueryClient();
  const updateCurrentRoom = useRoomStore((s) => s.updateCurrentRoom);

  return useMutation({
    mutationFn: ({ roomId, newHostUserId }: { roomId: string; newHostUserId: string }) =>
      roomService.transferHost(roomId, newHostUserId),
    onSuccess: (data) => {
      updateCurrentRoom(data.room);
      queryClient.setQueryData(queryKeys.rooms.detail(data.room.id), { room: data.room });
    },
  });
}

export function useRemoveParticipantMutation() {
  const queryClient = useQueryClient();
  const updateCurrentRoom = useRoomStore((s) => s.updateCurrentRoom);
  const setParticipants = useParticipantStore((s) => s.setParticipants);

  return useMutation({
    mutationFn: ({ roomId, userId }: { roomId: string; userId: string }) =>
      roomService.removeParticipant(roomId, userId),
    onSuccess: (data, variables) => {
      useParticipantStore.getState().removeParticipant(variables.userId);
      if (data.room) {
        updateCurrentRoom(data.room);
        setParticipants(data.room.participants);
        queryClient.setQueryData(queryKeys.rooms.detail(data.room.id), { room: data.room });
      }
    },
  });
}

export function getRoomErrorMessage(error: unknown): string {
  if (error instanceof ApiClientError) {
    switch (error.code) {
      case 'ROOM_FULL':
        return 'This room is full';
      case 'ROOM_WRONG_PASSWORD':
        return 'Incorrect password';
      case 'ROOM_NOT_FOUND':
        return 'Room not found';
      case 'ROOM_ALREADY_JOINED':
        return 'You are already in this room';
      case 'ROOM_DELETED':
        return 'Room has been deleted';
      case 'ROOM_HOST_CANNOT_LEAVE':
        return 'Transfer host role before leaving';
      case 'ROOM_NOT_MEMBER':
        return 'You are not in this room';
      case 'CSRF_INVALID':
        return 'Session expired — please refresh the page';
      default:
        return error.message;
    }
  }
  return 'Something went wrong';
}
